import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatNumber, formatPrice } from "@/lib/format";

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadCheckout() {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = CHECKOUT_SRC;
    s.onload = resolve;
    s.onerror = () => reject(new Error("The payment window couldn't load. Check your connection and try again."));
    document.body.appendChild(s);
  });
}

function useBuy() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const refresh = () => ["me", "usage", "billing-orders"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const buy = async (pack) => {
    setBusy(pack.id);
    try {
      const order = await api.post("/billing/orders", { pack_id: pack.id });
      await loadCheckout();
      const rzp = new window.Razorpay({
        key: order.key_id, order_id: order.razorpay_order_id, amount: order.amount, currency: order.currency,
        name: "ChatSleek", description: `${formatNumber(pack.credits)} credits`, prefill: order.prefill, theme: { color: "#0b0b0f" },
        // Credits are added only after the server verifies the signature and confirms the capture with Razorpay.
        handler: async (resp) => {
          try {
            const res = await api.post(`/billing/orders/${order.order_id}/verify`, resp);
            toast.success(res.duplicate ? "Payment already applied." : `${formatNumber(res.credits_added)} credits added.`);
          } catch (e) {
            toast.error(e.message);
          } finally {
            refresh();
          }
        },
        modal: { ondismiss: () => refresh() },
      });
      rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "The payment didn't go through. No credits were added."));
      rzp.open();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  return { buy, busy };
}

export function CreditPacks() {
  const { data } = useQuery({ queryKey: ["billing-packs"], queryFn: () => api.get("/billing/packs"), staleTime: 60_000 });
  const { buy, busy } = useBuy();
  const enabled = data?.payments_enabled;
  return (
    <div data-testid="credit-packs">
      {data && !enabled && (
        <p className="mb-3 text-xs text-zinc-500" data-testid="payments-disabled-note">Buying credits isn't switched on yet. Please check back soon.</p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(data?.items || []).map((p) => (
          <div key={p.id} className="flex flex-col rounded-xl border border-white/[0.08] bg-ink-850 p-5" data-testid={`credit-pack-${p.id}`}>
            <span className="text-sm font-medium text-white">{p.name}</span>
            <div className="mt-3 text-2xl font-semibold text-white">{formatNumber(p.credits)} <span className="text-sm font-normal text-zinc-500">credits</span></div>
            <div className="mt-1 text-sm text-zinc-400" data-testid={`credit-pack-price-${p.id}`}>{formatPrice(p.price_amount / 100, p.currency)} · one-time</div>
            <button
              type="button" disabled={!enabled || Boolean(busy)} onClick={() => buy(p)}
              className="mt-5 flex h-10 items-center justify-center gap-2 rounded-full bg-white text-sm font-medium text-ink-950 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-zinc-500"
              data-testid={`credit-pack-buy-${p.id}`}
            >
              {busy === p.id && <Loader2 className="h-4 w-4 animate-spin" />} Buy
            </button>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-zinc-600">Purchased credits don't expire. Payments are processed securely by our payment partner.</p>
    </div>
  );
}
